import { useAppDispatch, useAppSelector } from 'app/hooks';
import store from 'app/store';
import { airHandlerTypeCodes } from 'data/constants';
import {
  AirHandlerOptionsResponseType,
  BulkValidCondenserPayloadType,
  BulkValidSystemsResponseType,
  CondenserType,
  ConfigurationType,
  ValidDuctlessSystemsInputType,
  ValidSystemType
} from 'data/types';
import { setConfigurations } from 'features/configurations';
import {
  removeStaleSystemCombinations,
  ServerFilterOptionCountsType,
  ServerFilterUniverseType,
  setCombinationPagination,
  setDeadEndForConfig,
  setFilterOptionCountsForConfig,
  setFilterUniverseForConfig,
  setScenarioWinnersValid,
  setSystemCombinationsFull,
  setSystemCombinationsValid,
  settleCustomSystemsRevision
} from 'features/systems';
import {
  addFailedValidSystemsConfigId,
  addFetchingCondenserIds,
  addFetchingFullConfigIds,
  removeAllFailedValidSystemsConfigIdSources,
  removeFailedValidSystemsConfigId,
  removeFetchingCondenserIds,
  removeFetchingFullConfigIds,
  setGalleryPage,
  setGalleryPagination,
  setPreferredAirHandlerErrors
} from 'features/ui';
import useHybridLeftoverSpaceIds from 'hooks/use-hybrid-leftover-space-ids';
import useIsCompatibilityPathway from 'hooks/use-is-compatibility-pathway';
import { ValidSystemsDeadEndType } from 'types/dead-end';
import { produce } from 'immer';
import { useEffect, useMemo, useRef } from 'react';
import axios from 'utils/api';
import { normalizeError } from 'utils/error-log/serialize';
import {
  getAirHandlerPoolState,
  hasCondenserAwaitingAirHandlerAvailability,
  hasCurrentAirHandlerPool,
  withAirHandlerPoolState
} from 'utils/air-handler-availability';
import { AirHandlerOptionsWithPoolType } from 'utils/air-handler-availability-types';
import { BulkAirHandlerOptionsResponseType } from 'utils/air-handler-options-response-types';
import { everyZoneWithItemsHasAirHandler } from 'utils/air-handler-step-complete';
import {
  createAvailabilityRefreshTimer,
  logAvailabilityRefresh
} from 'utils/availability-refresh-timing';
import { markCondensersStale } from 'utils/configurations';
import { selectDuctedDesignConfigurationIds } from 'utils/ducted-design';
import { isHybridOnlySystemPreference, isPtacPreference } from 'utils/project';
import { findSpaceById, getRestrictedAirHandlerTypes } from 'utils/structure';
import { categoryWeightsFromProjectSettings } from 'utils/system-gallery-filters';
import { getOutdoorVisibility } from 'utils/systems';
import {
  BACKGROUND_PAGE_SIZE,
  pruneValidSystemsRequests,
  registerValidSystemsRequest
} from 'utils/valid-systems-request-registry';
import { isScoringDegradedResponse } from 'utils/valid-systems-settlement';
import { selectAutosavePending } from './autosave-pending';
import { equipmentFilterRequestProps } from './equipment-filter-request-props';
import { fullFetchConfigScope } from './full-fetch-scope';
import {
  buildValidSystemsInput,
  ValidSystemsRequestBase
} from './valid-systems-input';
import {
  buildPoolHash,
  buildStableHash,
  collectPoolHashes
} from './valid-systems-pool-hash';

const EMPTY_AIR_HANDLER_OPTIONS: AirHandlerOptionsResponseType = {
  summary: {
    count: 0,
    minPrice: null,
    maxPrice: null,
    minSEER2: null,
    maxSEER2: null,
    minCondenser: null,
    maxCondenser: null
  },
  perZoneTypes: {}
};

// The page-0 response metadata the published BulkValidSystemsResponseType
// predates (filterUniverse, filterOptionCounts and the dead-end diagnosis).
// TODO(AHA-6707): drop once the @alpinehomeair/common publish lands.
// Cast once at the top of applyValidResponse (review #4775) so the reads
// there are plain property access.
type BulkValidSystemsResponseWithPage0Meta = BulkValidSystemsResponseType & {
  filterUniverse?: ServerFilterUniverseType;
  filterOptionCounts?: ServerFilterOptionCountsType;
  deadEnd?: ValidSystemsDeadEndType;
};

type ValidSystemsUpdaterProps = {
  loading: boolean;
};

type InFlightEntry = {
  controller: AbortController;
  hash: string;
};

const poolKeyFlag = (
  options: CondenserType['airHandlerOptions']
): '_' | 'p' | 'P' => {
  const pool = getAirHandlerPoolState(options);
  if (!pool) return '_';
  return pool.stale ? 'P' : 'p';
};

export default function ValidSystemsUpdater({
  loading
}: ValidSystemsUpdaterProps) {
  const dispatch = useAppDispatch();
  const { locationData, instanceUniqueId, projectSettings } = useAppSelector(
    (state) => state.project
  );
  const { configurations, activeConfig } = useAppSelector(
    (state) => state.configurations
  );
  const autosavePending = useAppSelector(selectAutosavePending);
  const ductedDesignIds = useAppSelector(selectDuctedDesignConfigurationIds);
  const activeConfiguration = configurations[activeConfig];
  const {
    condenserProducts,
    supplementalHeatingWeight,
    supplementalCoolingWeight,
    supplementalHeatingWeightLoaded,
    criticalDataError
  } = useAppSelector((state) => state.data);
  const { structure } = useAppSelector((state) => state.structure);
  const leftoverSpaceIds = useHybridLeftoverSpaceIds();
  const {
    gallerySort,
    galleryPage,
    galleryPageSize,
    matchingAirHandlersMode,
    galleryFilters,
    equipmentFilters,
    activeTab
  } = useAppSelector((state) => state.ui);
  const customSystemsRevisionByConfig = useAppSelector(
    (state) => state.systems.customSystemsRevisionByConfig
  );
  // Ducted path: the legacy systemTypes dimension is inert (AHA-6681 QA F6)
  // and is dropped from both request bodies below. Derived from
  // projectSettings, which is already an effect dependency.
  const isDucted = useIsCompatibilityPathway();
  const validSystemsRetryByConfig = useAppSelector(
    (state) => state.systems.validSystemsRetryByConfig
  );

  const lastRequestHashRef = useRef<Map<string, string>>(new Map());
  const lastFailedHashRef = useRef<Map<string, string>>(new Map());
  const inFlightControllersRef = useRef<Map<string, InFlightEntry>>(new Map());
  const refreshTimerRef = useRef(createAvailabilityRefreshTimer());
  const lastGalleryHashRef = useRef<Map<string, string>>(new Map());
  const lastFailedGalleryHashRef = useRef<Map<string, string>>(new Map());
  const inFlightGalleryControllersRef = useRef<Map<string, InFlightEntry>>(
    new Map()
  );
  // Air-handler options (the cheap pool aggregate): keyed by the per-condenser
  // POOL hashes, so a type pick alone never re-requests.
  const lastOptionsHashRef = useRef<Map<string, string>>(new Map());
  const lastFailedOptionsHashRef = useRef<Map<string, string>>(new Map());
  const inFlightOptionsControllersRef = useRef<Map<string, InFlightEntry>>(
    new Map()
  );
  // Set when a full request was held back by a pending autosave; the effect
  // run that sees the save settle then fires without the debounce, so the
  // hold costs only the save itself.
  const fullFetchHeldForSaveRef = useRef(false);

  useEffect(() => {
    const controllers = inFlightControllersRef.current;
    const galleryControllers = inFlightGalleryControllersRef.current;
    const optionsControllers = inFlightOptionsControllersRef.current;
    return () => {
      controllers.forEach((entry) => entry.controller.abort());
      controllers.clear();
      galleryControllers.forEach((entry) => entry.controller.abort());
      galleryControllers.clear();
      optionsControllers.forEach((entry) => entry.controller.abort());
      optionsControllers.clear();
    };
  }, []);

  const configurationsStructureKey = useMemo(
    () =>
      configurations
        .map(
          (c) =>
            `${c.uniqueId}|${c.condensers
              .map((co) => {
                const pos =
                  co.position !== undefined
                    ? `${co.position.x},${co.position.y}`
                    : 'no';
                const zones = co.zones
                  .map((z) => {
                    const items = z.zoneItems
                      .map((zi) => zi.structureItemId)
                      .join(',');
                    const ah = z.airHandler?.airHandlerTypeCode ?? '_';
                    // zone.type feeds requiresDuctedAirHandler emission, so
                    // an in-session type change must retrigger the request.
                    return `${z.uniqueId}~${items}~${ah}~${z.type}`;
                  })
                  .join('+');
                const fetched = co.validSystemsFetched ? 'F' : 'S';
                // While the full fetch is gated, validSystemsFetched never
                // flips; a geometry edit shows up as the options pool going
                // stale (markCondensersStale), which must retrigger the
                // options request.
                const poolFlag = poolKeyFlag(co.airHandlerOptions);
                return `${co.uniqueId}:${fetched}:${poolFlag}:${pos}:${zones}`;
              })
              .join('!')}`
        )
        .join(';'),
    [configurations]
  );

  const stringPref = (raw: unknown): string | null =>
    typeof raw === 'string' && raw !== '' ? raw : null;

  const aestheticPreference = stringPref(
    projectSettings.find((s) => s.code === 'aesthetic_preference')?.value
  );

  // Strict-hybrid gate for per-zone requiresDuctedAirHandler emission:
  // ducted zones outside a committed Hybrid preference (e.g. debug-menu
  // structure import) must keep the pre-flag ductless bodies. Reactive:
  // projectSettings is already an effect dependency.
  const emitDuctedZoneFlags = isHybridOnlySystemPreference(
    projectSettings.find((s) => s.code === 'system_preference')?.value
  );

  // Request-body construction lives in valid-systems-input.ts (pure,
  // store-free, unit-tested); this wrapper just binds the store-derived
  // context.
  const buildCondenserInput = (
    condenser: CondenserType
  ): ValidDuctlessSystemsInputType | null =>
    buildValidSystemsInput(condenser, {
      structure,
      supplementalHeatingWeight,
      supplementalCoolingWeight,
      emitDuctedZoneFlags,
      leftoverSpaceIds
    });

  const collectZoneUniqueIds = (cond: CondenserType): string[] =>
    cond.zones.filter((z) => z.zoneItems.length > 0).map((z) => z.uniqueId);

  const requestStillMatchesLiveConfig = (
    configUniqueId: string,
    requestedCondenserIds: string[]
  ): boolean => {
    const latestConfig = store
      .getState()
      .configurations.configurations.find((c) => c.uniqueId === configUniqueId);
    if (!latestConfig) return false;

    const liveEligibleIds = new Set(
      latestConfig.condensers
        .filter(
          (c) => c.position !== undefined && buildCondenserInput(c) !== null
        )
        .map((c) => c.uniqueId)
    );

    if (liveEligibleIds.size !== requestedCondenserIds.length) return false;
    return requestedCondenserIds.every((id) => liveEligibleIds.has(id));
  };

  const customerPreferences = {
    aestheticPreference
  };

  const categoryWeightMultipliers =
    categoryWeightsFromProjectSettings(projectSettings);

  const sortBy: 'price-asc' | 'price-desc' | undefined =
    gallerySort === 'price-asc' || gallerySort === 'price-desc'
      ? gallerySort
      : undefined;

  // The gallery view is a prefix of the full page-0 response whenever it
  // uses the default sort, page 0 and a page no larger than the background
  // page: same body, same server ordering. Only a diverging view (price
  // sort, later page, larger page) is worth a second full request.
  const galleryDerivableFromFull =
    sortBy === undefined &&
    galleryPage === 0 &&
    galleryPageSize <= BACKGROUND_PAGE_SIZE;

  const buildBulkValidPayloads = (
    condensers: CondenserType[],
    configurationUniqueId: string
  ): BulkValidCondenserPayloadType[] =>
    condensers
      .map((cond): BulkValidCondenserPayloadType | null => {
        const fresh = buildCondenserInput(cond);
        if (!fresh) return null;
        const concealedRunSignature = cond.zones.map((zone) => ({
          zoneUniqueId: zone.uniqueId,
          additionalSupplies: zone.zoneItems.map((zi) => ({
            structureItemId: zi.structureItemId,
            runIds: (zi.additionalSupplyRuns ?? [])
              .map((r) => r.uniqueId)
              .sort((a, b) => a.localeCompare(b))
          })),
          additionalReturns: (zone.additionalReturnRuns ?? [])
            .map((r) => r.uniqueId)
            .sort((a, b) => a.localeCompare(b))
        }));
        const requestHash = buildStableHash({
          zipCode: locationData?.zipcode ?? '',
          instanceUniqueId,
          configurationUniqueId,
          systemInput: fresh,
          customerPreferences,
          categoryWeights: categoryWeightMultipliers,
          concealedRunSignature,
          condenserGeometry: {
            positionX: cond.position?.x ?? null,
            positionY: cond.position?.y ?? null,
            levelUniqueId: cond.levelUniqueId ?? null,
            heightOffGround: cond.heightOffGround ?? null,
            placementType: cond.placementType ?? null
          }
        });
        return {
          condenserUniqueId: cond.uniqueId,
          requestHash,
          zoneUniqueIds: collectZoneUniqueIds(cond),
          fresh
        };
      })
      .filter((p): p is BulkValidCondenserPayloadType => p !== null);

  const postBulk = async <T extends object>(
    url: 'valid-systems' | 'air-handler-options',
    body: Record<string, unknown>,
    abortController: AbortController
  ): Promise<{
    data: T | null;
    failed: boolean;
  }> => {
    if (!locationData?.zipcode) return { data: null, failed: false };
    try {
      const response = (
        await axios.post(url, body, {
          signal: abortController.signal
        })
      ).data as T;
      return { data: response, failed: false };
    } catch (error: any) {
      if (error.name === 'CanceledError') {
        return { data: null, failed: false };
      }
      console.error(
        `[valid-systems-watcher] ${url} request failed`,
        normalizeError(error)
      );
      return { data: null, failed: true };
    }
  };

  const fetchBulkValid = (
    body: Record<string, unknown>,
    abortController: AbortController
  ) =>
    postBulk<BulkValidSystemsResponseType>(
      'valid-systems',
      body,
      abortController
    );

  // The four keys every request body opens with, typed against the published
  // contract: instanceUniqueId is `string | undefined` there, so the key is
  // omitted (never sent as null) while the instance has not loaded.
  const baseRequestBody = (
    config: ConfigurationType,
    condensers: BulkValidCondenserPayloadType[]
  ): ValidSystemsRequestBase => ({
    zipCode: locationData?.zipcode ?? '',
    ...(instanceUniqueId ? { instanceUniqueId } : {}),
    configurationUniqueId: config.uniqueId,
    condensers
  });

  const currentPoolHashOf = (condenser: CondenserType): string | null => {
    const fresh = buildCondenserInput(condenser);
    return fresh ? buildPoolHash(fresh) : null;
  };

  // The options endpoint owns airHandlerOptions / ductingSummary: the pool
  // provenance recorded here is what lets a later type pick narrow locally
  // and what tells a geometry edit to wait for a fresh pool.
  const applyAirHandlerOptionsResponse = (
    config: ConfigurationType,
    optionsRes: BulkAirHandlerOptionsResponseType,
    requestedPoolHashByCondenserId: ReadonlyMap<string, string>
  ) => {
    const byCondenserId = new Map(
      optionsRes.perCondenser.map((pc) => [pc.condenserUniqueId, pc] as const)
    );
    const latestConfigs = store.getState().configurations.configurations;
    const currentPoolHashByCondenserId = new Map(
      (
        latestConfigs.find((c) => c.uniqueId === config.uniqueId)?.condensers ??
        []
      ).map((cond) => [cond.uniqueId, currentPoolHashOf(cond)] as const)
    );
    dispatch(
      setConfigurations(
        produce(latestConfigs, (draft) => {
          const configDraft = draft.find((c) => c.uniqueId === config.uniqueId);
          if (!configDraft) return;
          configDraft.condensers.forEach((cond) => {
            const perCondenser = byCondenserId.get(cond.uniqueId);
            if (!perCondenser) return;
            const options: AirHandlerOptionsWithPoolType = {
              ...perCondenser.airHandlerOptions,
              ...(perCondenser.radialDuctPassiveSpaces
                ? {
                    radialDuctPassiveSpaces:
                      perCondenser.radialDuctPassiveSpaces
                  }
                : {})
            };
            /* eslint-disable no-param-reassign */
            cond.airHandlerOptions = withAirHandlerPoolState(
              options,
              requestedPoolHashByCondenserId.get(cond.uniqueId),
              currentPoolHashByCondenserId.get(cond.uniqueId) ?? null
            );
            cond.ductingSummary = perCondenser.ductingSummary ?? null;
            /* eslint-enable no-param-reassign */
          });
        })
      )
    );
    logAvailabilityRefresh(
      config.uniqueId,
      refreshTimerRef.current.settle(config.uniqueId)
    );
  };

  const updateConfigurationAirHandlerOptions = async (
    config: ConfigurationType,
    isActive: boolean
  ) => {
    const positionedCondensers = config.condensers.filter(
      (cond) => cond.position !== undefined
    );
    if (positionedCondensers.length === 0) return;

    const condensersPayload = buildBulkValidPayloads(
      positionedCondensers,
      config.uniqueId
    );
    if (condensersPayload.length === 0) return;

    const requestedPoolHashes = collectPoolHashes(condensersPayload);
    const requestHash = buildStableHash({
      zipCode: locationData?.zipcode ?? '',
      configurationUniqueId: config.uniqueId,
      pools: [...requestedPoolHashes.entries()].sort(([a], [b]) =>
        a.localeCompare(b)
      ),
      _retryNonce: validSystemsRetryByConfig[config.uniqueId] ?? 0
    });
    // A hydration (updateConfigZoneItems) or a stale mark can drop or stale
    // a condenser's pool without changing the pool inputs; the hash alone
    // would then never let the options come back.
    const hasCondenserWithoutCurrentPool = positionedCondensers.some(
      (cond) =>
        requestedPoolHashes.has(cond.uniqueId) &&
        !hasCurrentAirHandlerPool(cond)
    );
    if (
      !hasCondenserWithoutCurrentPool &&
      lastOptionsHashRef.current.get(config.uniqueId) === requestHash
    ) {
      return;
    }
    if (lastFailedOptionsHashRef.current.get(config.uniqueId) === requestHash) {
      return;
    }

    const inFlight = inFlightOptionsControllersRef.current.get(config.uniqueId);
    if (inFlight?.hash === requestHash) return;
    if (inFlight) inFlight.controller.abort();
    const abortController = new AbortController();
    inFlightOptionsControllersRef.current.set(config.uniqueId, {
      controller: abortController,
      hash: requestHash
    });

    const fetchingIds = isActive
      ? positionedCondensers
          .filter(
            (cond) =>
              requestedPoolHashes.has(cond.uniqueId) &&
              !hasCurrentAirHandlerPool(cond)
          )
          .map((cond) => cond.uniqueId)
      : [];
    if (fetchingIds.length > 0) {
      dispatch(addFetchingCondenserIds(fetchingIds));
    }

    const requestBody = baseRequestBody(config, condensersPayload);
    const requestedCondenserIds = condensersPayload.map(
      (p) => p.condenserUniqueId
    );

    try {
      const { data: optionsRes, failed } =
        await postBulk<BulkAirHandlerOptionsResponseType>(
          'air-handler-options',
          requestBody,
          abortController
        );

      if (abortController.signal.aborted) return;

      if (
        !requestStillMatchesLiveConfig(config.uniqueId, requestedCondenserIds)
      ) {
        console.warn(
          `[valid-systems-watcher] discarding stale air-handler options for config ${config.uniqueId}; ` +
            'positioned condenser set changed mid-flight.'
        );
        return;
      }

      if (failed) {
        lastFailedOptionsHashRef.current.set(config.uniqueId, requestHash);
        dispatch(
          addFailedValidSystemsConfigId({
            configId: config.uniqueId,
            source: 'options'
          })
        );
        return;
      }

      if (!optionsRes) return;

      lastOptionsHashRef.current.set(config.uniqueId, requestHash);
      lastFailedOptionsHashRef.current.delete(config.uniqueId);
      dispatch(
        removeFailedValidSystemsConfigId({
          configId: config.uniqueId,
          source: 'options'
        })
      );
      applyAirHandlerOptionsResponse(config, optionsRes, requestedPoolHashes);
    } finally {
      if (fetchingIds.length > 0) {
        dispatch(removeFetchingCondenserIds(fetchingIds));
      }
      if (
        inFlightOptionsControllersRef.current.get(config.uniqueId)
          ?.controller === abortController
      ) {
        inFlightOptionsControllersRef.current.delete(config.uniqueId);
      }
    }
  };

  const applyValidResponse = (
    config: ConfigurationType,
    validRes: BulkValidSystemsResponseType,
    requestBodyHash: string,
    skipCombinations: boolean,
    isActive: boolean
  ) => {
    // One cast for all three page-0 metadata reads below (review #4775).
    const res = validRes as BulkValidSystemsResponseWithPage0Meta;

    // deadEnd is response metadata, not combination data: store it (or
    // clear a stale copy when absent) on EVERY accepted page-0 response.
    // The combinations block below is skipped for scoring-degraded and
    // partial-coverage responses — exactly the responses a diagnosis
    // explains — so this dispatch must sit outside it. Load-more responses
    // never reach this path. The Choose Systems list reads it through
    // deadEndForConfigs (spec 001 T004).
    dispatch(
      setDeadEndForConfig({
        configId: config.uniqueId,
        deadEnd: res.deadEnd
      })
    );

    const positionedCondensers = config.condensers.filter(
      (cond) => cond.position !== undefined
    );
    const uncoveredCondenserIds = positionedCondensers
      .filter((cond) => buildCondenserInput(cond) === null)
      .map((cond) => cond.uniqueId);
    const hasFullCoverage = uncoveredCondenserIds.length === 0;

    if (hasFullCoverage && skipCombinations) {
      console.warn(
        `[valid-systems-watcher] skipping combination dispatch for config ${config.uniqueId}; ` +
          'response carries valid systems but no scenarios/combinations (scoring degraded).'
      );
    }

    if (hasFullCoverage && !skipCombinations) {
      dispatch(
        setSystemCombinationsFull({
          configId: config.uniqueId,
          combinations: validRes.combinations
        })
      );

      // Page-0 response: reset pagination (loadedPage 0, loadingMore false)
      // and record the hash so load-more appends only chain onto this exact
      // request body.
      dispatch(
        setCombinationPagination({
          configId: config.uniqueId,
          total: validRes.pagination.total,
          requestBodyHash
        })
      );

      // Store the server-derived filter universe (or clear it when an older
      // API build omits it). Load-more responses never reach this path —
      // page 0 always precedes them with an identical universe.
      dispatch(
        setFilterUniverseForConfig({
          configId: config.uniqueId,
          filterUniverse: res.filterUniverse
        })
      );

      // Same contract for the what-if option counts: page-0 only, and an
      // absent field (older API build) clears the stored copy.
      dispatch(
        setFilterOptionCountsForConfig({
          configId: config.uniqueId,
          filterOptionCounts: res.filterOptionCounts
        })
      );

      dispatch(
        setScenarioWinnersValid({
          configId: config.uniqueId,
          scenarioWinners: validRes.scenarioWinners
        })
      );

      // Same data the gallery request would have returned (first gallery
      // page of the same sorted list), without the second request.
      if (galleryDerivableFromFull) {
        dispatch(
          setSystemCombinationsValid({
            configId: config.uniqueId,
            combinations: validRes.combinations.slice(0, galleryPageSize)
          })
        );
        if (isActive) {
          dispatch(
            setGalleryPagination({
              page: 0,
              pageSize: galleryPageSize,
              total: validRes.pagination.total
            })
          );
          dispatch(
            removeFailedValidSystemsConfigId({
              configId: config.uniqueId,
              source: 'gallery'
            })
          );
        }
      }
    } else if (!hasFullCoverage) {
      console.warn(
        `[valid-systems-watcher] skipping combination/scenario-winner dispatch for config ${config.uniqueId}; ` +
          'positioned condensers without zone-items would yield partial combinations:',
        uncoveredCondenserIds
      );
    }

    if (validRes.perCondenser.length === 0) return;

    const validByCondenserId = new Map<string, ValidSystemType[]>();
    validRes.perCondenser.forEach((pc) => {
      const systems = pc.systems.map((system) => {
        const next = { ...system, configId: config.uniqueId };
        const condenserProduct = condenserProducts.find(
          (product) => product.productId === system.condenserProductId
        );
        if (condenserProduct) {
          next.outdoorVisibility = getOutdoorVisibility(
            condenserProduct.dimensions
          );
        }
        return next;
      });
      validByCondenserId.set(pc.condenserUniqueId, systems);
    });

    // airHandlerOptions / ductingSummary are owned by the options request
    // (applyAirHandlerOptionsResponse); the full response only lands rows.
    const latestConfigs = store.getState().configurations.configurations;
    dispatch(
      setConfigurations(
        produce(latestConfigs, (draft) => {
          const configDraft = draft.find((c) => c.uniqueId === config.uniqueId);
          if (!configDraft) return;
          configDraft.condensers.forEach((cond) => {
            const systems = validByCondenserId.get(cond.uniqueId);
            if (!systems) return;
            /* eslint-disable no-param-reassign */
            cond.validSystems = systems;
            cond.validDuctedSystems = [];
            cond.validSystemsFetched = true;
            /* eslint-enable no-param-reassign */
          });
        })
      )
    );
  };

  const updateConfigurationValidSystems = async (
    config: ConfigurationType,
    isActive: boolean
  ) => {
    const positionedCondensers = config.condensers.filter(
      (cond) => cond.position !== undefined
    );
    if (positionedCondensers.length === 0) return;

    const localEmptyCount = positionedCondensers.filter(
      (cond) => buildCondenserInput(cond) === null
    ).length;

    const emptyCondenserIds = positionedCondensers
      .filter(
        (cond) =>
          !cond.validSystemsFetched && buildCondenserInput(cond) === null
      )
      .map((cond) => cond.uniqueId);

    if (emptyCondenserIds.length > 0) {
      const latestConfigs = store.getState().configurations.configurations;
      const idSet = new Set(emptyCondenserIds);
      dispatch(
        setConfigurations(
          produce(latestConfigs, (draft) => {
            const configDraft = draft.find(
              (c) => c.uniqueId === config.uniqueId
            );
            if (!configDraft) return;
            configDraft.condensers.forEach((cond) => {
              if (!idSet.has(cond.uniqueId)) return;
              /* eslint-disable no-param-reassign */
              cond.validSystems = [];
              cond.validDuctedSystems = [];
              cond.validSystemsFetched = true;
              cond.airHandlerOptions = EMPTY_AIR_HANDLER_OPTIONS;
              /* eslint-enable no-param-reassign */
            });
          })
        )
      );
    }

    const condensersPayload = buildBulkValidPayloads(
      positionedCondensers,
      config.uniqueId
    );
    if (condensersPayload.length === 0) {
      if (localEmptyCount > 0) {
        dispatch(
          addFailedValidSystemsConfigId({
            configId: config.uniqueId,
            source: 'full'
          })
        );
      }
      return;
    }

    const customSystemsRevision =
      customSystemsRevisionByConfig[config.uniqueId] ?? 0;

    const requestBody: Record<string, unknown> = {
      ...baseRequestBody(config, condensersPayload),
      customerPreferences,
      categoryWeights: categoryWeightMultipliers,
      // Server ignores; bumped by bumpCustomSystemsRevision on every
      // custom-system mutation so the dedup hash misses and the gallery
      // refetches (custom systems aren't otherwise part of the body).
      customSystemsRevision
    };

    const galleryFilterProps = {
      matchingAirHandlersMode,
      inStock: galleryFilters.inStock || undefined,
      onSale: galleryFilters.onSale || undefined,
      priceMin: galleryFilters.priceMin ?? undefined,
      priceMax: galleryFilters.priceMax ?? undefined
    };

    const equipmentFilterProps = equipmentFilterRequestProps(
      equipmentFilters,
      isDucted
    );

    Object.assign(requestBody, {
      page: 0,
      pageSize: BACKGROUND_PAGE_SIZE,
      ...galleryFilterProps,
      ...equipmentFilterProps
    });

    const requestBodyHash = buildStableHash({
      ...requestBody,
      _localEmptyCount: localEmptyCount,
      _retryNonce: validSystemsRetryByConfig[config.uniqueId] ?? 0
    });
    const hasUnfetchedRequestedCondenser = condensersPayload.some((payload) => {
      const cond = positionedCondensers.find(
        (c) => c.uniqueId === payload.condenserUniqueId
      );
      return cond ? !cond.validSystemsFetched : false;
    });
    if (
      !hasUnfetchedRequestedCondenser &&
      lastRequestHashRef.current.get(config.uniqueId) === requestBodyHash
    ) {
      dispatch(
        removeFailedValidSystemsConfigId({
          configId: config.uniqueId,
          source: 'full'
        })
      );
      return;
    }
    if (lastFailedHashRef.current.get(config.uniqueId) === requestBodyHash) {
      dispatch(
        addFailedValidSystemsConfigId({
          configId: config.uniqueId,
          source: 'full'
        })
      );
      return;
    }

    const inFlight = inFlightControllersRef.current.get(config.uniqueId);
    if (inFlight?.hash === requestBodyHash) {
      return;
    }
    if (inFlight) inFlight.controller.abort();
    const abortController = new AbortController();
    inFlightControllersRef.current.set(config.uniqueId, {
      controller: abortController,
      hash: requestBodyHash
    });
    dispatch(addFetchingFullConfigIds([config.uniqueId]));

    const staleCondenserIdSet = new Set(
      positionedCondensers
        .filter((cond) => !cond.validSystemsFetched)
        .map((cond) => cond.uniqueId)
    );

    const fetchingIds = isActive ? [...staleCondenserIdSet] : [];
    if (fetchingIds.length > 0) {
      dispatch(addFetchingCondenserIds(fetchingIds));
    }

    try {
      if (isActive) {
        console.log(
          '[valid-systems-watcher] Updating active configuration valid systems...'
        );
        config.condensers.forEach((condenser) => {
          const newErrorMessages: { zoneId: string; error: string }[] = [];
          condenser.zones.forEach((zone) => {
            const restrictedAirHandlerTypes = getRestrictedAirHandlerTypes(
              zone,
              structure
            );
            zone.zoneItems.forEach((zoneItem) => {
              const structureItem = findSpaceById(
                structure,
                zoneItem.structureItemId
              );
              if (structureItem) {
                const preferredAirHandlerType = structureItem.attributes.find(
                  (a) => a.code === 'preferred_air_handler_type'
                )?.value;
                restrictedAirHandlerTypes.forEach((type) => {
                  if (type === airHandlerTypeCodes[preferredAirHandlerType]) {
                    newErrorMessages.push({
                      zoneId: zone.uniqueId,
                      error: `One or more spaces in this zone cannot use a ${preferredAirHandlerType} air handler.`
                    });
                  }
                });
              }
            });
          });
          dispatch(setPreferredAirHandlerErrors(newErrorMessages));
        });
      } else {
        console.log(
          `[valid-systems-watcher] Updating background configuration ${config.uniqueId} valid systems...`
        );
      }

      const requestedCondenserIds = condensersPayload.map(
        (p) => p.condenserUniqueId
      );

      const { data: validRes, failed } = await fetchBulkValid(
        requestBody,
        abortController
      );

      if (abortController.signal.aborted) return;

      if (
        !requestStillMatchesLiveConfig(config.uniqueId, requestedCondenserIds)
      ) {
        console.warn(
          `[valid-systems-watcher] discarding stale full response for config ${config.uniqueId}; ` +
            'positioned condenser set changed mid-flight.'
        );
        return;
      }

      if (failed) {
        lastFailedHashRef.current.set(config.uniqueId, requestBodyHash);
        dispatch(
          addFailedValidSystemsConfigId({
            configId: config.uniqueId,
            source: 'full'
          })
        );
        return;
      }

      if (!validRes) return;

      const hasEmptyCondenser = validRes.perCondenser.some(
        (pc) => pc.systems.length === 0
      );
      const scoringDegraded = isScoringDegradedResponse(validRes);
      const shouldShowBanner =
        hasEmptyCondenser || localEmptyCount > 0 || scoringDegraded;
      if (shouldShowBanner) {
        lastFailedHashRef.current.set(config.uniqueId, requestBodyHash);
        lastRequestHashRef.current.delete(config.uniqueId);
      } else {
        lastRequestHashRef.current.set(config.uniqueId, requestBodyHash);
        lastFailedHashRef.current.delete(config.uniqueId);
      }
      dispatch(
        shouldShowBanner
          ? addFailedValidSystemsConfigId({
              configId: config.uniqueId,
              source: 'full'
            })
          : removeFailedValidSystemsConfigId({
              configId: config.uniqueId,
              source: 'full'
            })
      );

      registerValidSystemsRequest(
        config.uniqueId,
        requestBody,
        requestBodyHash
      );
      applyValidResponse(
        config,
        validRes,
        requestBodyHash,
        scoringDegraded,
        isActive
      );
    } finally {
      if (fetchingIds.length > 0) {
        dispatch(removeFetchingCondenserIds(fetchingIds));
      }
      if (
        inFlightControllersRef.current.get(config.uniqueId)?.controller ===
        abortController
      ) {
        inFlightControllersRef.current.delete(config.uniqueId);
        dispatch(removeFetchingFullConfigIds([config.uniqueId]));
        dispatch(
          settleCustomSystemsRevision({
            configId: config.uniqueId,
            revision: customSystemsRevision
          })
        );
      }
    }
  };

  const updateActiveGalleryCombinations = async (config: ConfigurationType) => {
    const positionedCondensers = config.condensers.filter(
      (cond) => cond.position !== undefined
    );
    if (positionedCondensers.length === 0) return;

    const localEmptyCount = positionedCondensers.filter(
      (cond) => buildCondenserInput(cond) === null
    ).length;

    const condensersPayload = buildBulkValidPayloads(
      positionedCondensers,
      config.uniqueId
    );
    if (condensersPayload.length === 0) {
      if (localEmptyCount > 0) {
        dispatch(
          addFailedValidSystemsConfigId({
            configId: config.uniqueId,
            source: 'gallery'
          })
        );
      }
      return;
    }

    const requestBody: Record<string, unknown> = {
      ...baseRequestBody(config, condensersPayload),
      customerPreferences,
      categoryWeights: categoryWeightMultipliers,
      sortBy,
      page: galleryPage,
      pageSize: galleryPageSize,
      matchingAirHandlersMode,
      inStock: galleryFilters.inStock || undefined,
      onSale: galleryFilters.onSale || undefined,
      priceMin: galleryFilters.priceMin ?? undefined,
      priceMax: galleryFilters.priceMax ?? undefined,
      customSystemsRevision:
        customSystemsRevisionByConfig[config.uniqueId] ?? 0,
      ...equipmentFilterRequestProps(equipmentFilters, isDucted)
    };

    const requestBodyHash = buildStableHash({
      ...requestBody,
      _localEmptyCount: localEmptyCount,
      _retryNonce: validSystemsRetryByConfig[config.uniqueId] ?? 0
    });
    if (lastGalleryHashRef.current.get(config.uniqueId) === requestBodyHash) {
      dispatch(
        removeFailedValidSystemsConfigId({
          configId: config.uniqueId,
          source: 'gallery'
        })
      );
      return;
    }
    if (
      lastFailedGalleryHashRef.current.get(config.uniqueId) === requestBodyHash
    ) {
      dispatch(
        addFailedValidSystemsConfigId({
          configId: config.uniqueId,
          source: 'gallery'
        })
      );
      return;
    }

    const inFlight = inFlightGalleryControllersRef.current.get(config.uniqueId);
    if (inFlight?.hash === requestBodyHash) {
      return;
    }
    if (inFlight) inFlight.controller.abort();
    const abortController = new AbortController();
    inFlightGalleryControllersRef.current.set(config.uniqueId, {
      controller: abortController,
      hash: requestBodyHash
    });

    try {
      const requestedCondenserIds = condensersPayload.map(
        (p) => p.condenserUniqueId
      );

      const { data: validRes, failed } = await fetchBulkValid(
        requestBody,
        abortController
      );

      if (abortController.signal.aborted) return;

      if (
        !requestStillMatchesLiveConfig(config.uniqueId, requestedCondenserIds)
      ) {
        console.warn(
          `[valid-systems-watcher] discarding stale gallery response for config ${config.uniqueId}; ` +
            'positioned condenser set changed mid-flight.'
        );
        return;
      }

      if (failed) {
        lastFailedGalleryHashRef.current.set(config.uniqueId, requestBodyHash);
        dispatch(
          addFailedValidSystemsConfigId({
            configId: config.uniqueId,
            source: 'gallery'
          })
        );
        return;
      }

      if (!validRes) return;

      const hasEmptyCondenser = validRes.perCondenser.some(
        (pc) => pc.systems.length === 0
      );
      const scoringDegraded = isScoringDegradedResponse(validRes);
      const shouldShowBanner =
        hasEmptyCondenser || localEmptyCount > 0 || scoringDegraded;
      if (shouldShowBanner) {
        lastFailedGalleryHashRef.current.set(config.uniqueId, requestBodyHash);
        lastGalleryHashRef.current.delete(config.uniqueId);
      } else {
        lastGalleryHashRef.current.set(config.uniqueId, requestBodyHash);
        lastFailedGalleryHashRef.current.delete(config.uniqueId);
      }
      dispatch(
        shouldShowBanner
          ? addFailedValidSystemsConfigId({
              configId: config.uniqueId,
              source: 'gallery'
            })
          : removeFailedValidSystemsConfigId({
              configId: config.uniqueId,
              source: 'gallery'
            })
      );

      if (scoringDegraded) return;

      dispatch(
        setSystemCombinationsValid({
          configId: config.uniqueId,
          combinations: validRes.combinations
        })
      );
      dispatch(setGalleryPagination(validRes.pagination));
      if (validRes.pagination.page !== galleryPage) {
        dispatch(setGalleryPage(validRes.pagination.page));
      }
    } finally {
      if (
        inFlightGalleryControllersRef.current.get(config.uniqueId)
          ?.controller === abortController
      ) {
        inFlightGalleryControllersRef.current.delete(config.uniqueId);
      }
    }
  };

  useEffect(() => {
    if (!activeConfiguration) return;
    const positioned = activeConfiguration.condensers.filter(
      (c) => c.position !== undefined && c.validSystemsFetched
    );
    if (positioned.length === 0) return;
    dispatch(setGalleryPage(0));
    const latest = store.getState().configurations.configurations;
    const ids = positioned.map((c) => c.uniqueId);
    dispatch(
      setConfigurations(
        latest.map((c) =>
          c.uniqueId === activeConfiguration.uniqueId
            ? markCondensersStale(c, ids)
            : c
        )
      )
    );
  }, [aestheticPreference]);

  const configurationIdsKey = configurations.map((c) => c.uniqueId).join('|');
  useEffect(() => {
    const currentIds = new Set(configurations.map((c) => c.uniqueId));
    const refMap = lastRequestHashRef.current;
    Array.from(refMap.keys()).forEach((key) => {
      if (!currentIds.has(key)) {
        refMap.delete(key);
      }
    });
    const failedRefMap = lastFailedHashRef.current;
    Array.from(failedRefMap.keys()).forEach((key) => {
      if (!currentIds.has(key)) {
        failedRefMap.delete(key);
      }
    });
    const controllerMap = inFlightControllersRef.current;
    Array.from(controllerMap.keys()).forEach((key) => {
      if (!currentIds.has(key)) {
        controllerMap.get(key)?.controller.abort();
        controllerMap.delete(key);
      }
    });
    const galleryRefMap = lastGalleryHashRef.current;
    Array.from(galleryRefMap.keys()).forEach((key) => {
      if (!currentIds.has(key)) {
        galleryRefMap.delete(key);
      }
    });
    const failedGalleryRefMap = lastFailedGalleryHashRef.current;
    Array.from(failedGalleryRefMap.keys()).forEach((key) => {
      if (!currentIds.has(key)) {
        failedGalleryRefMap.delete(key);
      }
    });
    const galleryControllerMap = inFlightGalleryControllersRef.current;
    Array.from(galleryControllerMap.keys()).forEach((key) => {
      if (!currentIds.has(key)) {
        galleryControllerMap.get(key)?.controller.abort();
        galleryControllerMap.delete(key);
      }
    });
    [lastOptionsHashRef.current, lastFailedOptionsHashRef.current].forEach(
      (hashMap) => {
        Array.from(hashMap.keys()).forEach((key) => {
          if (!currentIds.has(key)) {
            hashMap.delete(key);
          }
        });
      }
    );
    const optionsControllerMap = inFlightOptionsControllersRef.current;
    Array.from(optionsControllerMap.keys()).forEach((key) => {
      if (!currentIds.has(key)) {
        optionsControllerMap.get(key)?.controller.abort();
        optionsControllerMap.delete(key);
      }
    });
    pruneValidSystemsRequests(currentIds);
    Object.keys(store.getState().ui.failedValidSystemsConfigSources).forEach(
      (id) => {
        if (!currentIds.has(id)) {
          dispatch(removeAllFailedValidSystemsConfigIdSources(id));
        }
      }
    );
    dispatch(removeStaleSystemCombinations(Array.from(currentIds)));
  }, [configurationIdsKey]);

  useEffect(() => {
    configurations.forEach((config) => {
      if (hasCondenserAwaitingAirHandlerAvailability(config.condensers)) {
        refreshTimerRef.current.markStale(config.uniqueId);
      }
    });
    const releasingHeldFullFetch =
      fullFetchHeldForSaveRef.current && !autosavePending;
    const validSystemsTimeout = setTimeout(
      () => {
        // Gate on the settings load so request payloads never race the
        // supplemental heating weight fetch with the transient default,
        // and never fire under a critical data failure.
        if (
          loading ||
          !locationData ||
          !supplementalHeatingWeightLoaded ||
          criticalDataError
        )
          return;
        // PTAC designs are matched per room on Choose Systems, never
        // through the mini-split valid-systems query.
        if (isPtacPreference()) return;
        // On Choose Systems, unchecked configurations skip the full request
        // until they are checked again (see fullFetchConfigScope).
        const fullFetchScope = fullFetchConfigScope({
          activeTab,
          configIds: equipmentFilters.configIds,
          configurations
        });
        fullFetchHeldForSaveRef.current = false;
        configurations.forEach((config) => {
          if (ductedDesignIds.has(config.uniqueId)) return;
          const positioned = config.condensers.filter(
            (c) => c.position !== undefined
          );
          if (positioned.length === 0) return;
          const isActive = config.uniqueId === activeConfiguration?.uniqueId;
          // The pool aggregate serves the air-handler step on every edit; the
          // full (scored, combined) request waits until the step is complete —
          // the same predicate as the air_handlers step's requirementsMet.
          updateConfigurationAirHandlerOptions(config, isActive);
          if (!everyZoneWithItemsHasAirHandler(config)) return;
          if (fullFetchScope !== null && !fullFetchScope.has(config.uniqueId)) {
            return;
          }
          // The server scores against the structure and configuration rows
          // in the database, not the request body, and the autosave of the
          // same edit is still in flight at this point: wait for it so the
          // scoring context (and the server's pool cache key) is built from
          // the rows the user is looking at.
          if (autosavePending) {
            fullFetchHeldForSaveRef.current = true;
            return;
          }
          updateConfigurationValidSystems(config, isActive);
          if (isActive && !galleryDerivableFromFull) {
            updateActiveGalleryCombinations(config);
          }
        });
      },
      releasingHeldFullFetch ? 0 : 500
    );
    return () => {
      clearTimeout(validSystemsTimeout);
    };
  }, [
    configurationsStructureKey,
    // Payload heating data (space/zone heating weights) is derived from live
    // structure attributes; refetch on structure edits and let the per-config
    // request hash suppress calls when the payload is unchanged.
    structure,
    activeConfiguration?.uniqueId,
    locationData,
    loading,
    gallerySort,
    galleryPage,
    galleryPageSize,
    matchingAirHandlersMode,
    galleryFilters,
    projectSettings,
    customSystemsRevisionByConfig,
    validSystemsRetryByConfig,
    equipmentFilters,
    activeTab,
    autosavePending,
    supplementalHeatingWeight,
    supplementalCoolingWeight,
    supplementalHeatingWeightLoaded,
    criticalDataError,
    leftoverSpaceIds,
    ductedDesignIds
  ]);

  return null;
}
